import React from 'react';
import { X, Calendar, Clock } from 'lucide-react';
import { CalendarData } from '../types/calendar';

interface CalendarWidgetProps {
  isDarkMode: boolean;
  onClose: () => void;
  calendarData?: CalendarData;
  layout?: 'sidebar' | 'embedded';
  containerClassName?: string;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({
  isDarkMode,
  onClose,
  calendarData,
  layout = 'sidebar',
  containerClassName = '',
}) => {
  const handleBookAppointment = (schedulingUrl: string) => {
    // Open Calendly booking page in new window
    window.open(schedulingUrl, '_blank', 'width=800,height=700');
  };

  // Only use real calendar data - no fallback
  const displaySlots = calendarData?.available_slots || [];
  const eventType = calendarData?.event_type;
  const isCalendarAvailable = calendarData?.available ?? false;
  const isEmbedded = layout === 'embedded';

  return (
    <div className={`${
      isEmbedded ? 'w-full h-full' : 'w-full lg:w-80 h-full fixed lg:relative inset-0 lg:inset-auto'
    } flex-shrink-0 ${
      isDarkMode 
        ? 'bg-gray-800' 
        : 'bg-white'
    } flex min-h-0 flex-col ${isEmbedded ? 'z-auto' : 'z-50 lg:z-auto'} ${containerClassName}`}>
      <div className="p-3 sm:p-4 flex-shrink-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={20} className={isDarkMode ? 'text-orange-300' : 'text-[#ed7203]'} />
            <h3 className={`font-semibold text-base sm:text-lg ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Termin vereinbaren
            </h3>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-gray-700 text-gray-400' 
                : 'hover:bg-gray-100 text-gray-500'
            }`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Calendar Preview */}
        <div className={`rounded-lg p-4 mb-4 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <div className={`text-sm font-medium mb-2 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            {eventType ? `${eventType.name} (${eventType.duration} min)` : 'Termin buchen'}
          </div>
          <div className={`text-xs ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {calendarData?.booking_instructions || 'Verfügbare Termine'}
          </div>
        </div>

        {/* Calendar Availability Status */}
        {!isCalendarAvailable && calendarData && (
          <div className={`mb-4 p-3 rounded-lg ${
            isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className={`text-sm ${
              isDarkMode ? 'text-yellow-300' : 'text-yellow-700'
            }`}>
              {calendarData.message || 'Kalender vorübergehend nicht verfügbar'}
            </div>
          </div>
        )}
      </div>

      {/* Scrollable Time Slots */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-4 touch-pan-y scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
        <div className="space-y-2">
          {displaySlots.length > 0 ? (
            <>
              {/* Show scroll hint if many slots */}
              {displaySlots.length > 5 && (
                <div className={`text-xs text-center py-2 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  {displaySlots.length} verfügbare Termine • Scrollen für mehr
                </div>
              )}
              {/* Real Calendly slots only */}
              {displaySlots.map((slot, index) => (
                <button
                  key={index}
                  onClick={() => handleBookAppointment(slot.scheduling_url)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Clock size={16} />
                    <div className="text-left">
                      <div className="font-medium">{new Date(slot.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      <div className="text-xs text-gray-400">{slot.formatted_time.split(' at ')[0]}</div>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    isDarkMode
                      ? 'bg-[#11607d]/30 text-[#28d1a2]'
                      : 'bg-[#e7f5f1] text-[#11607d]'
                  }`}>
                    Verfügbar
                  </span>
                </button>
              ))}
            </>
          ) : (
            // No calendar data available - show error message
            <div className={`text-center py-8 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <div className="mb-2">Keine Termine verfügbar</div>
              <div className="text-xs">Bitte versuchen Sie es später erneut</div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Info */}
      <div className="flex-shrink-0 p-4">
        {/* Calendar Integration Info */}
        <div className={`p-3 rounded-lg ${
          isDarkMode ? 'bg-[#ed7203]/15 border border-[#ed7203]/30' : 'bg-[#fff3e8] border border-[#f4d2b3]'
        }`}>
          <div className={`text-sm font-medium mb-1 ${
            isDarkMode ? 'text-orange-200' : 'text-[#c75f02]'
          }`}>
            {isCalendarAvailable ? 'Live Kalender-Integration' : 'Kalender-Integration'}
          </div>
          <div className={`text-xs ${
            isDarkMode ? 'text-orange-200' : 'text-[#c75f02]'
          }`}>
            {isCalendarAvailable
              ? 'Klicken Sie auf einen verfügbaren Termin um direkt über Calendly zu buchen.'
              : 'Verbinden Sie sich mit Calendly, Microsoft 365 oder Google Calendar für nahtlose Terminplanung.'
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarWidget;
